import { nestedNodeDelimiter, pcbLayoutRootNodeId, PcbNodeTypes, } from "@buildwithflux/models";
import * as parsel from "parsel-js";
import { get, set } from "../../cache";
import { ElementHelper, getDesignatorWithinContext } from "../../elements";
import { devAssertVar } from "../../helpers";
import { normalizePcbLayoutNodeType } from "../../helpers/normalizePcbLayoutNodeType";
import { getAncestorUids, PcbNodeHelper } from "../../nodes";
import { getDefaultValueProperty } from "../../property";
/**
 * NOTE: A new matcher should be created once per "bake" to refresh caches.
 */
export class PcbSelectorMatcher {
    elements;
    nets;
    pcbLayoutNodes;
    documentData;
    logger;
    objectDataCache = {};
    constructor(elements, nets = {}, pcbLayoutNodes, documentData, logger) {
        this.elements = elements;
        this.nets = nets;
        this.pcbLayoutNodes = pcbLayoutNodes;
        this.documentData = documentData;
        this.logger = logger;
    }
    /**
     * Simply get all matching node IDs for the given nodes and selector.
     */
    static getMatchingNodeUids(documentState, pcbLayoutNodes, selectorValue, logger) {
        if (!documentState)
            return [];
        const matcher = new PcbSelectorMatcher(documentState.elements, documentState.nets, pcbLayoutNodes, documentState, logger);
        // Pre-parsing this selector outside the loop saves ~60ms every time the user selects
        // a ruleset in a large project.
        const parsedSelectorResult = matcher.parseSelector(selectorValue);
        return Object.keys(pcbLayoutNodes).filter((nodeUid) => matcher.match(parsedSelectorResult, nodeUid));
    }
    /**
     * Attempt to match the given nodeUid to the given selector and return the
     * specificity of the match.
     *
     * @param fromSubdocNestedUid The full uid path of the subdoc from which the selector is used.
     * Why do this?
     * If the matching is happening in the context of a subdoc, we have to consider any designator remappping
     * that may have happened when the subdoc was imported into the project, then "undo" the map to keep
     * selectors working as expected.
     */
    match(selector, nodeUid, fromSubdocNestedUid, 
    // HACK: disables cache for getObjectData when it can be tainted with incomplete data
    // This can happen with getNetNodeFlattenedRules
    dontSaveCache = false) {
        const objectData = this.getObjectData(nodeUid, dontSaveCache);
        const nodeData = this.pcbLayoutNodes[nodeUid];
        // NOTE: nodeData === undefined is actually expected here, for example for matching on a docUid
        const nodeIsRoot = nodeData === undefined || nodeData.uid === pcbLayoutRootNodeId;
        const objectType = nodeIsRoot ? PcbNodeTypes.root : nodeData.type;
        // QUESTION: why is this needed? the type is defined
        if (selector) {
            let specificityNumber, parsedSelector;
            if (typeof selector === "string") {
                ({ specificityNumber, parsedSelector } = this.parseSelector(selector));
            }
            else {
                ({ specificityNumber, parsedSelector } = selector);
            }
            if (!parsedSelector) {
                return undefined;
            }
            // If a `nodeIsTerminal`, it should match either `element` type OR `pad` type, here we
            // 1. First match the original objectType - this holds for any node
            // 2. Then for a terminal node, we do another match for "element" type. By doing so, we can
            //    make sure that a terminal node can match either `element` or `pad`
            let matches = this.parsedSelectorMatches(parsedSelector, objectType, objectData, nodeUid, fromSubdocNestedUid);
            if (PcbNodeHelper.isTerminal(this.elements, nodeData)) {
                matches =
                    matches ||
                        this.parsedSelectorMatches(parsedSelector, PcbNodeTypes.element, objectData, nodeUid, fromSubdocNestedUid);
            }
            if (matches) {
                return specificityNumber;
            }
        }
    }
    cachedSelectorParsing(selector) {
        // TODO I don't think this cache is faster than just directly parsing the selector in most
        // cases! Would need to test exhaustively with many selectors across a number of documents
        // to be sure. In my initial tests, the differences aren't huge either way -- but certainly no
        // slower if we remove the caching.
        //
        // Would be worth removing the caching just for simplicity and reducing memory usage (and
        // to avoid increasing the size of a globally-used cache). But leaving for now because it
        // would need more testing and doesn't solve any urgent problems.
        //
        // Same applies to `cachedSelectorSimpleSpecificityParsing` below.
        const cacheKey = `cachedSelectorParsing:${selector}`;
        const cachedParsedSelector = get(cacheKey);
        if (cachedParsedSelector) {
            return cachedParsedSelector;
        }
        const parsedSelector = parsel.parse(selector);
        set(cacheKey, parsedSelector);
        return parsedSelector;
    }
    cachedSelectorSimpleSpecificityParsing(selector) {
        const cacheKey = `cachedSelectorSpecificityParsing:${selector}`;
        const cachedParsedSelectorSpecificity = get(cacheKey);
        if (cachedParsedSelectorSpecificity !== undefined) {
            return cachedParsedSelectorSpecificity;
        }
        try {
            const parsedSelectorSpecificity = parsel.specificity(selector);
            if (!parsedSelectorSpecificity) {
                throw new Error(`Invalid selector: parsel.specificity returned null for "${selector}"`);
            }
            const simpleSpecificity = parsel.specificityToNumber(parsedSelectorSpecificity);
            set(cacheKey, simpleSpecificity);
            return simpleSpecificity;
        }
        catch (error) {
            // Invalid selector syntax (e.g., "Opening paren without closing paren") - log error, return 0 specificity
            // and cache the result to avoid repeated parsing attempts
            if ("captureError" in this.logger) {
                this.logger.captureError(error);
            }
            else {
                this.logger.error(error);
            }
            set(cacheKey, 0);
            return 0;
        }
    }
    getObjectData(nodeUid, dontSaveCache = false) {
        if (this.pcbLayoutNodes[nodeUid]) {
            if (this.objectDataCache[nodeUid]) {
                return this.objectDataCache[nodeUid];
            }
            const objectData = this.getObjectDataFromNode(this.pcbLayoutNodes[nodeUid]);
            if (!dontSaveCache) {
                this.objectDataCache[nodeUid] = objectData;
            }
            return objectData;
        }
        else {
            // Node is root node; uid doesn't exist in pcbLayoutNodes index.
            // QUESTION: why not check for nodeUid === "ROOT" instead? that would be safer
            return this.documentData;
        }
    }
    getObjectDataFromNode(pcbLayoutNode) {
        switch (pcbLayoutNode.type) {
            case PcbNodeTypes.element:
            case PcbNodeTypes.pad: {
                // This is to get element data for normal elements
                if (!PcbNodeHelper.isNodeUnderSubLayout(pcbLayoutNode.uid)) {
                    return this.elements[pcbLayoutNode.uid] || pcbLayoutNode;
                }
                // If the element/pad is a nested one, grab element data from nested part version data cache!
                const elementData = ElementHelper.getNestedElementData(this.elements, pcbLayoutNode.uid);
                // TODO: We are grabing data from different places when matching a selector, some from elements, and some from pcbLayoutNodes.
                // And when considering nested data, this becomes very complicated to maintain and test. We should come up with a better solution
                // such that:
                // 1. It is very explicit that everything we need is just under that field. For example `element.dataForRuleSelectors`
                // 2. The structure should be the same for nested elements, so that we can have one simple function to iterate through and find
                //    the correspondint data for this node.
                // NOTE: here we are merging both for elements in a sublayout.
                // 1. `elementData` contains fields such as `label` and `properties` in order to match attribute
                //    selectors from its parent element. Example: ResistorA containing a terminal with properties, and
                //    In that ResistorA we define a global selector to pick that terminal via attribute selector
                // 2. `pcbLayoutNode` contains fields that __might__ be needed, such as `uid`, which is used to match
                //    selector generated by footprint - this is NOT obvious from code... and its very easy to miss.
                //    To imply it again: footprint assets will result in selectors such as `pad[uid="xxx"]`, which
                //    will try to match `uid` in pcbLayoutNode.
                // NOTE: elementData can be undefined if the nested element data is not found - handle gracefully
                return elementData ? { ...elementData, ...pcbLayoutNode } : pcbLayoutNode;
            }
            case PcbNodeTypes.net: {
                const schematicNet = this.nets[pcbLayoutNode.uid];
                // NOTE: schematicNet can be undefined if the net is not found - handle gracefully
                return schematicNet ? { ...pcbLayoutNode, ...schematicNet } : pcbLayoutNode;
            }
            default: {
                return pcbLayoutNode;
            }
        }
    }
    parsedSelectorMatches(parsedSelector, objectType, objectData, nodeUid, fromSubdocNestedUid) {
        if (parsedSelector.type === "id") {
            return this.idSelectorMatchesObjectData(parsedSelector, objectData, fromSubdocNestedUid);
        }
        else if (parsedSelector.type === "list") {
            return parsedSelector.list.some((subSelector) => this.parsedSelectorMatches(subSelector, objectType, objectData, nodeUid));
        }
        else if (parsedSelector.type === "attribute") {
            return this.attributeSelectorMatchesObjectData(parsedSelector, objectData);
        }
        else if (parsedSelector.type === "compound") {
            return this.compoundSelectorMatchesObjectData(parsedSelector, objectType, objectData);
        }
        else if (parsedSelector.type === "type") {
            return this.typeSelectorMatchesObjectData(parsedSelector, objectType);
        }
        else if (parsedSelector.type === "complex" && parsedSelector.combinator === ">") {
            const node = this.pcbLayoutNodes[nodeUid];
            if (!node)
                return false;
            const left = parsedSelector.left;
            const right = parsedSelector.right;
            let ancestorMatches = false;
            if (left) {
                const parentNodeData = this.pcbLayoutNodes[node.parentUid];
                if (parentNodeData) {
                    const parentNodeObjectData = this.getObjectDataFromNode(parentNodeData);
                    ancestorMatches = this.parsedSelectorMatches(left, parentNodeData.type, parentNodeObjectData, nodeUid);
                }
            }
            return ancestorMatches && !!right && this.parsedSelectorMatches(right, objectType, objectData, nodeUid);
        }
        else if (parsedSelector.type === "complex" && parsedSelector.combinator === " ") {
            const node = this.pcbLayoutNodes[nodeUid];
            if (!node)
                return false;
            const left = parsedSelector.left;
            const right = parsedSelector.right;
            let ancestorMatches = false;
            if (left) {
                const ancestorUids = getAncestorUids(this.pcbLayoutNodes, node);
                ancestorMatches = ancestorUids.some((ancestorUid) => {
                    const ancestorNodeData = this.pcbLayoutNodes[ancestorUid];
                    if (ancestorNodeData) {
                        const ancestorNodeObjectData = this.getObjectDataFromNode(ancestorNodeData);
                        return this.parsedSelectorMatches(left, ancestorNodeData.type, ancestorNodeObjectData, nodeUid);
                    }
                    return false;
                });
            }
            return ancestorMatches && !!right && this.parsedSelectorMatches(right, objectType, objectData, nodeUid);
        }
        return false;
    }
    parseSelector(selector) {
        let specificityNumber = 0;
        let parsedSelector;
        try {
            specificityNumber = this.cachedSelectorSimpleSpecificityParsing(selector);
            // There is a interactive demo here to play with the result object: https://projects.verou.me/parsel
            parsedSelector = this.cachedSelectorParsing(selector);
        }
        catch (error) {
            if ("captureError" in this.logger) {
                this.logger.captureError(error);
            }
            else {
                this.logger.error(error);
            }
        }
        return { specificityNumber, parsedSelector };
    }
    /**
     * NOTE: This matches the parts of a compound selector in any order.
     * That's different from CSS.
     *
     * NOTE: Compound matches are not documented anywhere in
     * https://docs.flux.ai/reference/pcb-layout-rule-selectors#type-selector
     */
    compoundSelectorMatchesObjectData(individualSelector, objectKey, objectData) {
        const individualSelectorList = individualSelector.list;
        let matchedCompoundSelectors = 0;
        // eslint-disable-next-line @buildwithflux/no-array-foreach
        individualSelectorList.forEach((compoundSelectorEntry) => {
            const result = this.parseCompoundSelectorEntry(compoundSelectorEntry, objectKey, objectData);
            if (result) {
                matchedCompoundSelectors += 1;
            }
        });
        let matches = false;
        if (individualSelectorList.length === matchedCompoundSelectors) {
            matches = true;
        }
        return matches;
    }
    parseCompoundSelectorEntry(compoundSelector, objectKey, objectData) {
        if (compoundSelector.type === "type") {
            return this.typeSelectorMatchesObjectData(compoundSelector, objectKey);
        }
        else if (compoundSelector.type === "id") {
            return this.idSelectorMatchesObjectData(compoundSelector, objectData);
        }
        else if (compoundSelector.type === "attribute") {
            return this.attributeSelectorMatchesObjectData(compoundSelector, objectData);
        }
        else if (compoundSelector.type === "pseudo-class" && compoundSelector.name === "not") {
            return this.notSelectorMatchesObjectData(compoundSelector, objectData);
        }
        return false;
    }
    typeSelectorMatchesObjectData(individualSelector, objectType) {
        const selectorName = individualSelector.name || "";
        const selectorContent = individualSelector.content || "";
        return (selectorContent === "*" ||
            selectorName === objectType ||
            selectorName === normalizePcbLayoutNodeType(objectType));
    }
    /**
     * ID selectors match on the element label (AKA designator) or the node
     * name, which is copied from the element label or created in
     * "core/src/pcb/nodes/factory.ts". As a reminder, objectData can be a merger of
     * element and node data. See getObjectDataFromNode. The node name should
     * take precedence over the element label as it is potentially more
     * specific.
     *
     * @see https://docs.flux.ai/reference/pcb-layout-rule-selectors#designator-selector
     */
    idSelectorMatchesObjectData(individualSelector, objectData, fromSubdocNestedUid) {
        const selectorDesignator = individualSelector.name;
        const designatorInHostDoc = 
        // QUESTION: when would we use PcbNode.name instead of just always using PcbNode.label?
        "name" in objectData && objectData.name
            ? objectData.name
            : "label" in objectData && objectData.label
                ? objectData.label
                : undefined;
        // The only way this happens is if:
        // - the designator was manually removed in the host doc OR
        // - the element had no designator in its initial context (e.g. initial subdoc)
        //   and no new designator was assigned (e.g. missing designator prefix)
        // In both cases, it should fail designator matching.
        if (!designatorInHostDoc)
            return false;
        if (fromSubdocNestedUid) {
            const designatorInContext = getDesignatorWithinContext({
                logger: this.logger,
                allElements: this.elements,
                fromSubdocUid: fromSubdocNestedUid,
                nodeUid: objectData.uid,
                designatorInHostDoc,
            });
            if (!devAssertVar(designatorInContext, this.logger)) {
                return false;
            }
            return selectorDesignator.toLowerCase() == designatorInContext.toLowerCase();
        }
        else {
            return selectorDesignator.toLowerCase() == designatorInHostDoc.toLowerCase();
        }
    }
    attributeSelectorMatchesObjectData(compoundSelector, objectData) {
        if (compoundSelector.name === "uid" && "uid" in objectData) {
            return this.parseAttribute(compoundSelector, objectData.uid);
        }
        else if (compoundSelector.name === "name" && "name" in objectData) {
            return this.parseAttribute(compoundSelector, objectData.name);
        }
        else if (compoundSelector.name.match(/^[Dd]escription$/) && "description" in objectData) {
            // TODO: this doesn't match the part description (because the part description field is in
            // objectData.part_version_data_cache). What was the original intention for this?
            return this.parseAttribute(compoundSelector, objectData.description);
        }
        else if (compoundSelector.name.match(/^[Dd]esignator$/) && "label" in objectData) {
            return this.parseAttribute(compoundSelector, objectData.label);
        }
        else if (compoundSelector.name === "partName" && "part_version_data_cache" in objectData) {
            // NOTE: The usage of `partName` here is for backward compatibility for old project.
            return this.parseAttribute(compoundSelector, objectData.part_version_data_cache?.name || objectData.partName);
        }
        else if (compoundSelector.name === "current" && "current" in objectData) {
            return this.parseAttribute(compoundSelector, objectData.current);
        }
        else if (compoundSelector.name === "impedancePair" && "impedancePair" in objectData) {
            return this.parseAttribute(compoundSelector, objectData.impedancePair);
        }
        else if (compoundSelector.name === "impedance" && "impedance" in objectData) {
            return this.parseAttribute(compoundSelector, objectData.impedance);
        }
        if ("properties" in objectData) {
            const properties = objectData.properties;
            // TODO: property name matching is NOT supported for nested element/part if the property map only
            // contains values - this is the old setup we have for extracted_element_cache. In other words, this
            // is not supported for old project until user publish a new version.
            if (properties) {
                const matchingProperty = Object.values(properties).find((property) => {
                    if (typeof property === "object" && "name" in property) {
                        return property.name?.split(" ").join("") === compoundSelector.name;
                    }
                    return false;
                });
                if (matchingProperty) {
                    // Match property value for normal elements
                    return this.parseAttribute(compoundSelector, matchingProperty.value?.toString());
                }
            }
            if (compoundSelector.name === "defaultValue") {
                // Match default property value for nested elements
                if ("defaultPropertyValue" in objectData) {
                    return this.parseAttribute(compoundSelector, objectData.defaultPropertyValue);
                }
                // Match default property value for normal elements
                const defaultProperty = getDefaultValueProperty(properties);
                if (defaultProperty) {
                    return this.parseAttribute(compoundSelector, defaultProperty.value?.toString());
                }
            }
        }
        return false;
    }
    notSelectorMatchesObjectData(individualSelector, objectData) {
        const nodeType = "type" in objectData ? objectData.type : undefined;
        // we only support :not(node-type) for now
        return individualSelector.argument !== nodeType;
    }
    // Implements https://developer.mozilla.org/en-US/docs/Learn/CSS/Building_blocks/Selectors/Attribute_selectors
    parseAttribute(compoundSelector, value) {
        if (value) {
            const compoundSelectorValue = this.removeDoubleQuotes(compoundSelector.value || "");
            if (!compoundSelector.operator && !compoundSelectorValue) {
                // Matches elements with an attr attribute (whose name is the value in square brackets).
                return true;
            }
            else if (compoundSelector.operator === "=") {
                // Matches elements with an attribute whose value is an exact
                // match — the string inside the quotes - or, an exact match
                // after removing the nested ID prefix "<parentUid>__",
                // recursively, so that rules defined in sublayouts still work.
                // See getForeignRulesForNode.
                if (compoundSelectorValue === value) {
                    return true;
                }
                else if (compoundSelector.caseSensitive === "i" &&
                    compoundSelectorValue.toLowerCase() === value.toLowerCase()) {
                    return true;
                }
                else if (value.includes(nestedNodeDelimiter)) {
                    return this.parseAttribute(compoundSelector, value.split(nestedNodeDelimiter).slice(1).join(nestedNodeDelimiter));
                }
            }
            else if (compoundSelector.operator === "~=") {
                // Matches elements with an attr attribute whose value is exactly value, or contains value in its (space separated) list of values.
                if (compoundSelector.caseSensitive === "i") {
                    return (value.toLowerCase() === compoundSelectorValue.toLowerCase() ||
                        compoundSelectorValue.toLowerCase().split(" ").includes(value.toLowerCase()));
                }
                else {
                    return value === compoundSelectorValue || value.split(" ").includes(compoundSelectorValue);
                }
            }
            else if (compoundSelector.operator === "|=") {
                // Matches elements with an attr attribute whose value is exactly value or begins with value immediately followed by a hyphen.
                if (compoundSelector.caseSensitive === "i") {
                    return (value.toLowerCase() === compoundSelectorValue.toLowerCase() ||
                        `${value}-`.toLowerCase().startsWith(compoundSelectorValue.toLowerCase()));
                }
                else {
                    return value === compoundSelectorValue || value.startsWith(`${compoundSelectorValue}-`);
                }
            }
            else if (compoundSelector.operator === "^=") {
                // Matches elements with an attr attribute (whose name is the value in square brackets), whose value begins with value.
                if (compoundSelector.caseSensitive === "i") {
                    return value.toLowerCase().startsWith(compoundSelectorValue.toLowerCase());
                }
                else {
                    return value.startsWith(compoundSelectorValue);
                }
            }
            else if (compoundSelector.operator === "$=") {
                // Matches elements with an attr attribute whose value ends with value.
                if (compoundSelector.caseSensitive === "i") {
                    return value.toLowerCase().endsWith(compoundSelectorValue.toLowerCase());
                }
                else {
                    return value.endsWith(compoundSelectorValue);
                }
            }
            else if (compoundSelector.operator === "*=") {
                // Matches elements with an attr attribute whose value contains value anywhere within the string.
                if (compoundSelector.caseSensitive === "i") {
                    return value.toLowerCase().includes(compoundSelectorValue.toLowerCase());
                }
                else {
                    return value.includes(compoundSelectorValue);
                }
            }
        }
        return false;
    }
    removeDoubleQuotes(value) {
        if (value) {
            // QUESTION: would it be worth it to initalize the regex outside of
            // the loop? in testing, it saves ~10ms over 1M iterations
            // or maybe just use lodash trim?
            return value.replace(/^"(.+)"$/, "$1");
        }
        return value;
    }
}
//# sourceMappingURL=PcbSelectorMatcher.js.map